import {noop} from '@benchling/js-utils';
import React, {useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {redirectForDownload} from 'BenchlingUtil';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {RunOutputSamplesTable} from 'lab-automation/output-samples/RunOutputSamplesTableConstants';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import ManagedColumnsSimpleTableEditable from 'notebook/simple-tables/ManagedColumnsSimpleTableEditable';
import {standaloneProps} from 'notebook/simple-tables/SimpleTableEditable';
import {useActionFunctionContext} from 'notebook/structured-tables/action-functions/ActionFunctionContext';
import TableWithMissingSchema from 'notebook/structured-tables/components/TableWithMissingSchema';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {StructuredTableRow} from 'notebook/structured-tables/StructuredTableConstants';
import getTablePropsFromActionFunctions from 'notebook/structured-tables/util/getActionFunctions';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {TableHeaderControlsArgs} from 'notebook/tables/constants/TableConstants';
import {SimpleDownloadAsCSVButton} from 'notebook/tables/export/DownloadAsCSVButton';
import getPlainTextRawTable from 'notebook/tables/export/getPlainTextRawTable';
import {fetchOversizedNLADTable} from 'results/assays/AssayRecordActions';
import {RUN_OUTPUT_TABLE_ICON, RUN_OUTPUT_TABLE_LABEL} from 'results/assays/AssayRecordConstants';
import * as ExportUtil from 'util/ExportUtil';
import {initiateTextDownload} from 'util/TextToBlobUtil';

import outputSamplesTableToTable from './outputSamplesTableToTable';

interface RunOutputSamplesTableProps {
  getIdFromCid: (cid: string) => string | number | undefined;
  outputSamplesTable: RunOutputSamplesTable;
  viewer?: boolean;
}

export default function RunOutputSamplesTableView(props: RunOutputSamplesTableProps): JSX.Element {
  const [isCSVDownloadDisabled, setCSVDownloadDisabled] = useState(false);

  function renderDownloadAsCSVButton(): JSX.Element {
    return (
      <SimpleDownloadAsCSVButton disabled={isCSVDownloadDisabled} onDownloadAsCSV={() => onDownloadAsCSV()} />
    );
  }

  const actionFunctionContext = useActionFunctionContext();

  async function onDownloadAsCSV(): Promise<void> {
    setCSVDownloadDisabled(true);
    trackEvent(ELN_ANALYTICS_ACTIONS.DOWNLOADED_OUTPUT_SAMPLES_TABLE_AS_CSV);
    let cells: Array<Array<Cell>>;
    let downloadUrl: string = '';
    const {table} = outputSamplesTableToTable(outputSamplesTable);
    const columnLabels = table.columnLabels as Array<string>;
    if (outputSamplesTable.rows.length) {
      cells = table.table;
    } else {
      const oversizedTable = await fetchOversizedNLADTable(
        props.getIdFromCid(outputSamplesTable.cid) as string
      );
      cells = oversizedTable.rows.map((row: StructuredTableRow<{}>) => row.cells);
      downloadUrl = oversizedTable.url;
    }
    if (downloadUrl.length) {
      redirectForDownload(downloadUrl);
    } else {
      const exportedRows = getPlainTextRawTable({table: cells, headerRow: columnLabels});
      initiateTextDownload(ExportUtil.formatRows(exportedRows, ','), `${outputSamplesTable.name}.csv`);
    }
    setCSVDownloadDisabled(false);
  }

  function renderHeaderControls(args: TableHeaderControlsArgs, fileId: string | undefined): JSX.Element {
    if (args.isCollapsed) {
      return renderDownloadAsCSVButton();
    }
    const {rightHeaderControls} = getTablePropsFromActionFunctions({
      entryId: fileId ?? null,
      structuredTable: props.outputSamplesTable,
      adapter: getStructuredTableAdapters()[props.outputSamplesTable.kind],
      computedTable: args.computedTable,
      locked: false,
      updateHandler: noop,
      performTableOp: noop,
      actionFunctionContext,
      isFilterStale: false,
    });

    return (
      <>
        {rightHeaderControls(args)}
        {renderDownloadAsCSVButton()}
      </>
    );
  }

  const {outputSamplesTable} = props;
  const configName = outputSamplesTable.metadata.configName;
  const schema = outputSamplesTable.schemas[0];

  // Old ROS tables have a schema. If we have an old table *without* a schema, that
  // means the schema is inaccessible
  if (schema == null && configName.length === 0) {
    return (
      <TableWithMissingSchema
        icon={ModelTypeConstants.SAMPLE}
        indentation={0}
        tableName={RUN_OUTPUT_TABLE_LABEL}
        selectable={false}
      />
    );
  }

  const {table, widths} = outputSamplesTableToTable(outputSamplesTable);
  const isOversized = !outputSamplesTable.rows.length;

  return (
    <EditorPropertiesContext.Consumer>
      {({fileId}) => (
        <ManagedColumnsSimpleTableEditable
          {...standaloneProps}
          fileId={fileId ?? null}
          cid={outputSamplesTable.cid}
          key={schema ? schema.id : configName}
          locked={true}
          canAddRemoveColumns={false}
          tableLabel={RUN_OUTPUT_TABLE_LABEL}
          enableFormulas={false}
          forceCollapsed={isOversized}
          forceRowCount={isOversized ? outputSamplesTable.metadata.numSamples : undefined}
          headerControls={(args) => renderHeaderControls(args, fileId)}
          hideFormulaBox={true}
          icon={RUN_OUTPUT_TABLE_ICON}
          isCollapsible={true}
          isLeftAlignColumnHeader={true}
          initialColumnWidths={widths}
          tableWithoutColumnWidths={table}
          viewer={props.viewer}
          dataTestAttrs={{component: 'RunOutputSamplesView', element: 'root'}}
        />
      )}
    </EditorPropertiesContext.Consumer>
  );
}
